// React Router 7 data router for the admin console and capture tree.
//
// Layout:
//   /                    → RoleHomeRedirect dispatches by session role
//                          (admin → /admin/dashboard, field_staff →
//                          /capture; unauthenticated → /sign-in)
//   /logout-error        → public LogoutErrorPage (unguarded — must
//                          not pass through RequireAdmin, otherwise
//                          a cleared-but-failed logout silently
//                          redirects back to /auth/login. Also
//                          deliberately NOT under /auth/* because
//                          that prefix is claimed by the Vite dev
//                          proxy and the Encore backend.)
//   /sign-in             → public LoginPage (Microsoft Entra entry
//                          point + decorative email/password form;
//                          unguarded; same `/auth/*` reservation as
//                          /logout-error so the path lives at root).
//   /admin               → RequireAdmin > AdminLayout > Outlet
//     /admin/dashboard
//     /admin/locations
//     /admin/assets
//     /admin/templates              → TemplatesLayout > Outlet
//        index → redirect to ./rooms
//        /admin/templates/rooms     → RoomsPage
//        /admin/templates/buildings → BuildingTypesPage (scaffold)
//     /admin/reviews                → ReviewsLayout > Outlet
//        index → redirect to ./queue
//        /admin/reviews/queue       → ReviewsPage
//        /admin/reviews/exclusions  → ExclusionsPage
//     /admin/exclusions             → redirect to /admin/reviews/exclusions
//                                     (legacy URL, kept so bookmarks resolve)
//     /admin/exports
//   /capture             → RequireAuth > Outlet (field_staff OR admin)
//     /capture                               → CaptureHomePage
//     /capture/bases/:baseId                 → BaseDetailPage
//     /capture/sites/:siteId                 → SiteDetailPage
//     /capture/sites/:siteId/capture         → RequireAdminSiteCapture >
//                                              SiteCaptureScreen.
//                                              field_staff are redirected
//                                              back to /capture/sites/:siteId
//                                              — the buildings list is the
//                                              only supported entry point
//                                              for them. See PR #453 +
//                                              require-admin-site-capture.tsx.
//     /capture/buildings/:buildingId         → BuildingCaptureScreen
//     /capture/spaces/:spaceId               → RoomCaptureScreen
//   *                    → NotFoundPage
//
// Each guard reads the session from AuthProvider (which wraps the
// router in main.tsx), so the router tree itself does not need
// loaders for auth. Data loaders for each page land in later steps.

import { lazy, Suspense, type ReactElement } from 'react'
import type { RouteObject } from 'react-router'
import { createBrowserRouter, Navigate, Outlet } from 'react-router'

import { AdminLayout } from './admin-layout'
import { LoginPage } from './login-page'
import { LogoutErrorPage } from './logout-error-page'
import { NotFoundPage } from './not-found-page'
import { RequireAdmin } from './require-admin'
import { RequireAdminSiteCapture } from './require-admin-site-capture'
import { RequireAuth } from './require-auth'
import { RoleHomeRedirect } from './role-home-redirect'

import { Spinner } from '../ui/spinner'

const DashboardPage = lazy(() =>
  import('../admin/dashboard/dashboard-page').then((m) => ({
    default: m.DashboardPage,
  })),
)
const LocationsPage = lazy(() =>
  import('../admin/locations/locations-page').then((m) => ({
    default: m.LocationsPage,
  })),
)
const AssetsPage = lazy(() =>
  import('../admin/assets/assets-page').then((m) => ({
    default: m.AssetsPage,
  })),
)
const RoomsPage = lazy(() =>
  import('../admin/rooms/rooms-page').then((m) => ({
    default: m.RoomsPage,
  })),
)
const TemplatesLayout = lazy(() =>
  import('../admin/templates/templates-layout').then((m) => ({
    default: m.TemplatesLayout,
  })),
)
const BuildingTypesPage = lazy(() =>
  import('../admin/templates/building-types-page').then((m) => ({
    default: m.BuildingTypesPage,
  })),
)
const FloorsPage = lazy(() =>
  import('../admin/templates/floors-page').then((m) => ({
    default: m.FloorsPage,
  })),
)
const ReviewsLayout = lazy(() =>
  import('../admin/reviews/reviews-layout').then((m) => ({
    default: m.ReviewsLayout,
  })),
)
const ReviewsPage = lazy(() =>
  import('../admin/reviews/reviews-page').then((m) => ({
    default: m.ReviewsPage,
  })),
)
const ExclusionsPage = lazy(() =>
  import('../admin/exclusions/exclusions-page').then((m) => ({
    default: m.ExclusionsPage,
  })),
)
const ExportsPage = lazy(() =>
  import('../admin/exports/exports-page').then((m) => ({
    default: m.ExportsPage,
  })),
)
const BuildingBreakdownPage = lazy(() =>
  import('../admin/buildings/building-breakdown-page').then((m) => ({
    default: m.BuildingBreakdownPage,
  })),
)

import { CaptureErrorFallback } from '../capture/components/capture-error-boundary'
import { UnsyncedCaptureProvider } from '../capture/components/unsynced-capture-provider'
import { BaseDetailPage } from '../capture/pages/base-detail-page'
import { BuildingCaptureScreen } from '../capture/pages/building-capture-screen'
import { CaptureHomePage } from '../capture/pages/capture-home-page'
import { RoomCaptureScreen } from '../capture/pages/room-capture-screen'
import { SiteCaptureScreen } from '../capture/pages/site-capture-screen'
import { SiteDetailPage } from '../capture/pages/site-detail-page'
import {
  isAdminRouteDisabled,
  readDisabledAdminRoutes,
} from '../lib/admin-routes-disabled'

// Admin route names whose element should be replaced by <NotFoundPage />
// when their name appears in VITE_ADMIN_ROUTES_DISABLED. The route
// name matches the leaf `path:` string below so the env var format
// ("classification,rooms,reviews") stays obvious to operators.
interface GateableRoute {
  readonly path: string
  readonly element: ReactElement
}

// Templates and Reviews are special cases — each is a tabbed section
// with two children rather than a single leaf page. The gating system
// swaps the parent layout for <NotFoundPage /> when their token
// ('templates' / 'reviews') appears in VITE_ADMIN_ROUTES_DISABLED,
// which hides every sub-page in one go. Sub-routes are added
// separately below. Individual sub-tab tokens ('exclusions') gate
// just that sub-route + hide the matching tab from the layout.
const GATEABLE_ADMIN_ROUTES: readonly GateableRoute[] = [
  { path: 'dashboard', element: <DashboardPage /> },
  { path: 'locations', element: <LocationsPage /> },
  { path: 'assets', element: <AssetsPage /> },
  { path: 'exports', element: <ExportsPage /> },
]

export function buildRouterConfig(
  disabled: ReadonlySet<string> = readDisabledAdminRoutes(),
): RouteObject[] {
  return [
    {
      path: '/',
      element: <RoleHomeRedirect />,
    },
    {
      path: '/logout-error',
      element: <LogoutErrorPage />,
    },
    {
      // Public SPA login page. Path is /sign-in, NOT /auth/sign-in —
      // the /auth/* prefix is claimed by the Vite dev proxy and the
      // Encore backend (see vite.config.ts). The page itself is
      // primarily a Microsoft Entra entry point; the email/password
      // form is decorative until the backend exposes a credential
      // endpoint per ADR-012.
      path: '/sign-in',
      element: <LoginPage />,
    },
    {
      path: '/admin',
      element: (
        <Suspense
          fallback={
            <div className="flex min-h-screen items-center justify-center">
              <Spinner label="Loading admin console" size="lg" />
            </div>
          }
        >
          <RequireAdmin>
            <AdminLayout disabledRoutes={disabled} />
          </RequireAdmin>
        </Suspense>
      ),
      children: [
        { index: true, element: <Navigate to="dashboard" replace /> },
        ...GATEABLE_ADMIN_ROUTES.map(({ path, element }) => ({
          path,
          // Gated routes render the shared 404 page in place of
          // their real page element. The route still exists in the
          // tree so direct URLs resolve (rather than falling
          // through to the global catch-all and changing the URL
          // semantics), but the visible content is the 404.
          element: isAdminRouteDisabled(path, disabled) ? (
            <NotFoundPage />
          ) : (
            element
          ),
        })),
        // Read-only building breakdown — drilled into from the
        // dashboard at building depth. Path is nested under
        // /admin/dashboard/* (not /admin/buildings/*) because the
        // dev proxy claims the latter prefix for backend routes
        // like /admin/buildings/:buildingId/breakdown.
        {
          path: 'dashboard/buildings/:buildingId',
          element: <BuildingBreakdownPage />,
        },
        // Templates section: parent route renders TemplatesLayout
        // (header + tab strip + outlet), child routes provide the
        // tab content. Whole section disappears when 'templates' is
        // in VITE_ADMIN_ROUTES_DISABLED.
        {
          path: 'templates',
          element: isAdminRouteDisabled('templates', disabled) ? (
            <NotFoundPage />
          ) : (
            <TemplatesLayout />
          ),
          children: [
            { index: true, element: <Navigate to="rooms" replace /> },
            { path: 'rooms', element: <RoomsPage /> },
            { path: 'buildings', element: <BuildingTypesPage /> },
            { path: 'floors', element: <FloorsPage /> },
          ],
        },
        // Reviews section: parent route renders ReviewsLayout (header
        // + tab strip + outlet), child routes provide the tab content.
        // Whole section disappears when 'reviews' is in
        // VITE_ADMIN_ROUTES_DISABLED. The 'exclusions' token still
        // works as a fine-grained gate that hides only the Exclusions
        // sub-tab + 404s its sub-route.
        {
          path: 'reviews',
          element: isAdminRouteDisabled('reviews', disabled) ? (
            <NotFoundPage />
          ) : (
            <ReviewsLayout disabledRoutes={disabled} />
          ),
          children: [
            { index: true, element: <Navigate to="queue" replace /> },
            { path: 'queue', element: <ReviewsPage /> },
            {
              path: 'exclusions',
              element: isAdminRouteDisabled('exclusions', disabled) ? (
                <NotFoundPage />
              ) : (
                <ExclusionsPage />
              ),
            },
          ],
        },
        // Legacy URL — bookmarks pointing at the old standalone
        // /admin/exclusions resolve to the new tab. The redirect
        // honours the gate by routing through the same parent, so
        // disabling 'reviews' or 'exclusions' lands the user on the
        // 404 page rather than a dead redirect.
        {
          path: 'exclusions',
          element: <Navigate to="/admin/reviews/exclusions" replace />,
        },
      ],
    },
    {
      path: '/capture',
      element: (
        <RequireAuth>
          {/* Held-work indicator — its probe machine (poll + connector
              subscriptions) is mounted ONCE here via UnsyncedCaptureProvider,
              at the /capture PARENT. The parent persists across capture-screen
              navigations (base → site → building → room), so the machine
              outlives those transitions instead of being torn down and rebuilt
              per screen. The banner itself renders as the top row of each
              screen's sticky CaptureLayout header (reading this provider's
              context), so it stays pinned while the surveyor scrolls and
              self-hides when nothing is held (audit PR 732 / #5, #6, M1+M2).

              Contrast DroppedChangeToast, which is mounted app-wide in main.tsx
              (NOT here): a permanent drop can fire on /admin or mid-redirect, so
              its subscriber must outlive ALL routes. This banner is a capture
              concern — scoping it to /capture is correct. */}
          <UnsyncedCaptureProvider>
            <Outlet />
          </UnsyncedCaptureProvider>
        </RequireAuth>
      ),
      // A render throw in any capture screen (e.g. a malformed row from
      // the optimistic/echo reconciliation) is caught here and rendered
      // as a recoverable fallback instead of unmounting the subtree to a
      // blank screen. See capture-error-boundary.tsx.
      errorElement: <CaptureErrorFallback />,
      children: [
        { index: true, element: <CaptureHomePage /> },
        { path: 'bases/:baseId', element: <BaseDetailPage /> },
        { path: 'sites/:siteId', element: <SiteDetailPage /> },
        {
          path: 'sites/:siteId/capture',
          element: (
            <RequireAdminSiteCapture>
              <SiteCaptureScreen />
            </RequireAdminSiteCapture>
          ),
        },
        {
          path: 'buildings/:buildingId',
          element: <BuildingCaptureScreen />,
        },
        { path: 'spaces/:spaceId', element: <RoomCaptureScreen /> },
      ],
    },
    { path: '*', element: <NotFoundPage /> },
  ]
}

export const router = createBrowserRouter(buildRouterConfig())
